<template>
  <div class="csv-panel">
    <div class="panel-body">
      <div class="row g-3">

        <!-- Columna izquierda: carga del archivo -->
        <div class="col-8">
          <div
            class="drop-zone"
            :class="{ 'drag-over': arrastrando, 'has-file': archivoNombre }"
            @dragover.prevent="arrastrando = true"
            @dragleave="arrastrando = false"
            @drop.prevent="onDrop"
            @click="$refs.fileInput.click()"
          >
            <input ref="fileInput" type="file" accept=".csv" style="display:none" @change="onFileChange" />
            <span class="material-symbols-outlined drop-icon">
              {{ archivoNombre ? 'description' : 'upload_file' }}
            </span>
            <div v-if="archivoNombre" class="drop-filename">{{ archivoNombre }}</div>
            <div v-else class="drop-hint">
              Arrastra un CSV aquí o <span class="drop-link">haz clic para seleccionar</span>
            </div>
            <div class="drop-format">
              Formato esperado:
              <code>codigo; fecha (YYYY-MM-DD); cantidad; tipo (S/M/I)</code>
            </div>
          </div>

          <!-- Modo fecha -->
          <div class="mode-bar mt-2">
            <button type="button" class="mode-btn" :class="{ active: modoFecha === 'archivo' }" @click="modoFecha = 'archivo'">
              <span class="material-symbols-outlined" style="font-size:15px">table_view</span>
              Fecha del archivo
            </button>
            <button type="button" class="mode-btn" :class="{ active: modoFecha === 'manual' }" @click="modoFecha = 'manual'">
              <span class="material-symbols-outlined" style="font-size:15px">edit_calendar</span>
              Fecha manual
            </button>
          </div>

          <!-- Fecha manual -->
          <div v-if="modoFecha === 'manual'" class="manual-fecha mt-2">
            <label class="field-label">
              <span class="material-symbols-outlined" style="font-size:15px;vertical-align:middle">event</span>
              Fecha de atención (se aplicará a todas las filas)
            </label>
            <input type="date" class="form-control form-control-sm" style="max-width:180px"
              v-model="fechaManual" :min="fechaDesde" :max="fechaHasta" />
          </div>

          <!-- Tipo por defecto -->
          <div class="tipo-defecto mt-2">
            <label class="field-label">
              <span class="material-symbols-outlined" style="font-size:15px;vertical-align:middle">category</span>
              Tipo por defecto
              <span class="tipo-hint">(se usa cuando la columna tipo no está en el CSV)</span>
            </label>
            <div class="tipo-btns">
              <button
                v-for="t in tipos" :key="t.valor"
                type="button" class="tipo-btn"
                :class="{ active: tipoDefecto === t.valor }"
                @click="tipoDefecto = t.valor; revalidarFilas()"
              >
                <span class="material-symbols-outlined" style="font-size:15px">{{ t.icon }}</span>
                {{ t.label }}
              </button>
            </div>
          </div>

          <!-- Preview tabla -->
          <div v-if="filas.length" class="preview-table mt-3">
            <div class="preview-header">
              <span>Código</span>
              <span>Fecha</span>
              <span>Cant.</span>
              <span>Tipo</span>
              <span>Estado</span>
            </div>
            <div
              v-for="(fila, idx) in filas" :key="idx"
              class="preview-row"
              :class="{
                'row-error': fila.estado === 'error',
                'row-warn':  fila.estado === 'advertencia',
                'row-ok':    fila.estado === 'ok',
                'row-alt':   idx % 2 === 1,
              }"
            >
              <span class="item-code">{{ fila.codigo }}</span>
              <span class="fecha-cell">{{ fila.fechaEfectiva }}</span>
              <input
                type="number" class="cantidad-input" v-model.number="fila.cantidad" min="1"
                @change="fila.cantidad = Math.max(1, fila.cantidad || 1)"
                :disabled="fila.estado === 'error'"
              />
              <select class="tipo-select" v-model="fila.tipo"
                :disabled="fila.estado === 'error'" @change="fila.idElemento = null">
                <option v-for="t in tipos" :key="t.valor" :value="t.valor">{{ t.valor }} – {{ t.label }}</option>
              </select>
              <span class="estado-cell">
                <span v-if="fila.estado === 'ok'" class="chip chip-ok">
                  <span class="material-symbols-outlined" style="font-size:12px">check</span> OK
                </span>
                <span v-else-if="fila.estado === 'advertencia'" class="chip chip-warn" :title="fila.mensajeError">
                  <span class="material-symbols-outlined" style="font-size:12px">warning</span> {{ fila.mensajeError }}
                </span>
                <span v-else class="chip chip-err" :title="fila.mensajeError">
                  <span class="material-symbols-outlined" style="font-size:12px">close</span> {{ fila.mensajeError }}
                </span>
              </span>
            </div>
          </div>

          <div v-else class="tabla-vacia mt-3">
            <span class="material-symbols-outlined" style="font-size:32px;color:#ced4da">csv</span>
            <p class="mt-2 mb-0">Aún no se ha cargado ningún archivo</p>
          </div>
        </div>

        <!-- Columna derecha: resumen y acciones -->
        <div class="col-4 d-flex flex-column gap-3" style="padding-top:0.5rem;">
          <div v-if="filas.length" class="resumen-box">
            <div class="resumen-title">Resumen del archivo</div>
            <div class="resumen-row"><span>Total filas</span><strong>{{ filas.length }}</strong></div>
            <div class="resumen-row text-success"><span>✔ Válidas</span><strong>{{ filasValidas.length }}</strong></div>
            <div class="resumen-row text-warning" v-if="filasAdvertencia.length">
              <span>⚠ Advertencias</span><strong>{{ filasAdvertencia.length }}</strong>
            </div>
            <div class="resumen-row text-danger" v-if="filasConError.length">
              <span>✖ Con error</span><strong>{{ filasConError.length }}</strong>
            </div>
            <hr class="my-2" />
            <div class="resumen-row">
              <span>Modo fecha</span>
              <strong>{{ modoFecha === 'archivo' ? 'Del archivo' : 'Manual' }}</strong>
            </div>
            <div v-if="modoFecha === 'manual' && fechaManual" class="resumen-row">
              <span>Fecha aplicada</span><strong>{{ fechaManual }}</strong>
            </div>
            <hr class="my-2" />
            <div class="resumen-title" style="margin-top:2px">Por tipo</div>
            <div v-for="t in tipos" :key="t.valor" class="resumen-row">
              <span>{{ t.valor }} – {{ t.label }}</span>
              <strong>{{ contarPorTipo(t.valor) }}</strong>
            </div>
          </div>

          <button type="button" class="btn-action info" @click="descargarPlantilla" title="Descargar plantilla CSV">
            <span class="material-symbols-outlined">download</span> Plantilla CSV
          </button>
          <button type="button" class="btn-action secondary" @click="limpiar" :disabled="!filas.length">
            <span class="material-symbols-outlined">delete</span> Limpiar
          </button>
          <button type="button" class="btn-action success" @click="guardar" :disabled="!puedeGuardar"
            title="Registrar filas válidas en la planilla">
            <span class="material-symbols-outlined">save</span>
            Registrar {{ filasValidas.length }} fila(s)
          </button>

          <div v-if="guardando" class="progreso-box">
            <div class="progreso-label">
              <span class="material-symbols-outlined spin" style="font-size:14px">progress_activity</span>
              {{ progresoActual }} / {{ filasValidas.length }}
            </div>
            <div class="progress" style="height:6px;">
              <div class="progress-bar bg-success" :style="{ width: porcentajeProgreso + '%' }"></div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import api from "@/services/api";
import Swal from "sweetalert2";

const TIPOS = [
  { valor: "S", label: "Tarifario",    icon: "local_hospital" },
  { valor: "M", label: "Medicamentos", icon: "medication"     },
  { valor: "I", label: "Insumos",      icon: "inventory_2"    },
];

export default {
  name: "RegistroDesdeCSV",

  props: {
    idPlanilla:  { type: [Number, String], required: true },
    idPrestador: { type: [Number, String], required: true },
    idUsuario:   { type: [Number, String], required: true },
    fechaDesde:  { type: String, required: true },
    fechaHasta:  { type: String, required: true },
  },

  emits: ["guardado"],

  data() {
    return {
      arrastrando:     false,
      archivoNombre:   "",
      modoFecha:       "archivo",
      fechaManual:     "",
      tipoDefecto:     "S",
      filas:           [],
      guardando:       false,
      progresoActual:  0,
      tipos:           TIPOS,
      cacheTarifarios: {},   // { S: [...], M: [...], I: [...] }
    };
  },

  computed: {
    filasValidas()    { return this.filas.filter(f => f.estado === "ok" || f.estado === "advertencia"); },
    filasConError()   { return this.filas.filter(f => f.estado === "error"); },
    filasAdvertencia(){ return this.filas.filter(f => f.estado === "advertencia"); },
    porcentajeProgreso() {
      if (!this.filasValidas.length) return 0;
      return Math.round((this.progresoActual / this.filasValidas.length) * 100);
    },
    puedeGuardar() {
      if (!this.filasValidas.length || this.guardando) return false;
      if (this.modoFecha === "manual" && !this.fechaManual) return false;
      return true;
    },
  },

  watch: {
    modoFecha()     { this.recalcularFechasEfectivas(); },
    fechaManual()   { this.recalcularFechasEfectivas(); },
    fechaDesde(val) { if (val && !this.fechaManual) this.fechaManual = val; },
  },

  mounted() {
    if (this.fechaDesde) this.fechaManual = this.fechaDesde;
  },

  methods: {
    // ── Tipo ─────────────────────────────────────────────────────
    endpointPorTipo(tipo) {
      const map = {
        S: `/vwtarifapress/idprestador/${this.idPrestador}`,
        M: `/vwtarifapress/idprestador/10`,
        I: `/vwtarifapress/idprestador/11`,
      };
      return map[tipo] ?? map.S;
    },

    normalizarTipo(raw) {
      const val = (raw ?? "").trim().toUpperCase();
      return ["S", "M", "I"].includes(val) ? val : this.tipoDefecto;
    },

    contarPorTipo(tipo) {
      return this.filas.filter(f => f.tipo === tipo).length;
    },

    // ── Drag & Drop / File input ──────────────────────────────────
    onDrop(e) {
      this.arrastrando = false;
      const file = e.dataTransfer.files[0];
      if (file) this.procesarArchivo(file);
    },
    onFileChange(e) {
      const file = e.target.files[0];
      if (file) this.procesarArchivo(file);
      e.target.value = "";
    },
    procesarArchivo(file) {
      if (!file.name.endsWith(".csv")) {
        Swal.fire("Error", "Solo se aceptan archivos .csv", "error");
        return;
      }
      this.archivoNombre   = file.name;
      this.cacheTarifarios = {};
      const reader = new FileReader();
      reader.onload = (e) => { this.filas = this.parsearCSV(e.target.result); };
      reader.readAsText(file, "UTF-8");
    },

    // ── Parseo CSV ────────────────────────────────────────────────
    parsearCSV(texto) {
      const lineas = texto.split("\n").map(l => l.trim()).filter(l => l.length > 0);
      const inicio = this.esCabecera(lineas[0]) ? 1 : 0;
      return lineas.slice(inicio).map((linea, idx) => {
        const partes   = linea.split(";").map(p => p.trim());
        const codigo   = partes[0] ?? "";
        const fechaCSV = this.normalizarFecha(partes[1] ?? "");
        const cantidad = parseFloat(partes[2]) || 1;
        const tipo     = this.normalizarTipo(partes[3]);   // 4ª columna opcional
        const { estado, mensajeError } = this.validarFila(codigo, fechaCSV);
        return {
          lineaOriginal: idx + 1 + inicio,
          codigo,
          fechaCSV,
          fechaEfectiva: this.modoFecha === "manual" ? (this.fechaManual || "") : fechaCSV,
          cantidad:      Math.max(1, Math.round(cantidad)),
          tipo,
          estado,
          mensajeError,
          idElemento:    null,
        };
      });
    },

    // Acepta YYYY-MM-DD y DD/MM/YYYY → devuelve siempre YYYY-MM-DD
    normalizarFecha(raw) {
      if (!raw) return "";
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
        const [d, m, y] = raw.split("/");
        return `${y}-${m}-${d}`;
      }
      return raw;
    },

    esCabecera(linea) {
      if (!linea) return false;
      const primera = linea.split(";")[0].trim().toLowerCase();
      return ["codigo", "código", "code", "cod"].includes(primera);
    },

    validarFila(codigo, fecha) {
      if (!codigo) return { estado: "error", mensajeError: "Código vacío" };
      const fechaRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (this.modoFecha === "archivo") {
        if (!fechaRegex.test(fecha))
          return { estado: "error", mensajeError: "Fecha inválida" };
        if (this.fechaDesde && fecha < this.fechaDesde)
          return { estado: "advertencia", mensajeError: "Fuera de rango" };
        if (this.fechaHasta && fecha > this.fechaHasta)
          return { estado: "advertencia", mensajeError: "Fuera de rango" };
      }
      return { estado: "ok", mensajeError: "" };
    },

    revalidarFilas() {
      this.filas = this.filas.map(f => {
        const { estado, mensajeError } = this.validarFila(f.codigo, f.fechaCSV);
        return { ...f, estado, mensajeError, idElemento: null };
      });
    },

    recalcularFechasEfectivas() {
      this.filas = this.filas.map(f => ({
        ...f,
        fechaEfectiva: this.modoFecha === "manual" ? (this.fechaManual || "") : f.fechaCSV,
      }));
    },

    // ── Resolver idElemento con caché por tipo ────────────────────
    async resolverIdElemento(codigo, tipo) {
      if (!this.cacheTarifarios[tipo]) {
        try {
          const res = await api.get(this.endpointPorTipo(tipo));
          this.cacheTarifarios[tipo] = Array.isArray(res.data) ? res.data : [];
        } catch {
          this.cacheTarifarios[tipo] = [];
        }
      }
      const encontrado = this.cacheTarifarios[tipo].find(
        i => (i.elementoCodigo ?? "").toLowerCase() === codigo.toLowerCase()
      );
      return encontrado ? encontrado.idElemento : null;
    },

    // ── Guardar ───────────────────────────────────────────────────
    async guardar() {
      if (!this.puedeGuardar) return;
      const confirm = await Swal.fire({
        title: "¿Confirmar registro?",
        html:  `Se registrarán <strong>${this.filasValidas.length}</strong> fila(s) desde <em>${this.archivoNombre}</em>`,
        icon:  "question",
        showCancelButton: true, confirmButtonText: "Sí, registrar",
        cancelButtonText: "Cancelar", confirmButtonColor: "#198754",
      });
      if (!confirm.isConfirmed) return;

      this.guardando      = true;
      this.progresoActual = 0;
      const errores = [];
      let guardados = 0;

      for (const fila of this.filasValidas) {
        if (!fila.idElemento) {
          fila.idElemento = await this.resolverIdElemento(fila.codigo, fila.tipo);
        }
        if (!fila.idElemento) {
          errores.push(`Línea ${fila.lineaOriginal} — ${fila.codigo} [${fila.tipo}]: código no encontrado`);
          this.progresoActual++;
          continue;
        }
        const payload = {
          idPlanilla:    Number(this.idPlanilla),
          idElemento:    fila.idElemento,
          idUsuarioReg:  Number(this.idUsuario),
          descripcion:   "automático",
          cantidad:      fila.cantidad || 1,
          cantidadIni:   fila.cantidad || 1,
          fechaAtencion: this.modoFecha === "manual" ? this.fechaManual : fila.fechaCSV,
          fechaReg:      new Date().toISOString(),
          estado:        1,
        };
        try {
          await api.post("/planilladets", payload);
          guardados++;
        } catch (e) {
          errores.push(`Línea ${fila.lineaOriginal} — ${fila.codigo}: ${e.response?.data || e.message}`);
        }
        this.progresoActual++;
      }

      this.guardando = false;

      if (errores.length === 0) {
        Swal.fire("Éxito", `${guardados} elemento(s) registrado(s) correctamente`, "success");
        this.limpiar();
        this.$emit("guardado");
      } else {
        Swal.fire({
          title: `${guardados} guardado(s), ${errores.length} con error`,
          html:  `<ul style="text-align:left;font-size:13px;max-height:200px;overflow-y:auto">
                    ${errores.map(e => `<li>${e}</li>`).join("")}
                  </ul>`,
          icon: "warning",
        });
        this.$emit("guardado");
      }
    },

    // ── Limpiar ───────────────────────────────────────────────────
    limpiar() {
      this.filas           = [];
      this.archivoNombre   = "";
      this.progresoActual  = 0;
      this.cacheTarifarios = {};
    },

    // ── Plantilla CSV ─────────────────────────────────────────────
    descargarPlantilla() {
      const fecha = this.fechaDesde ? this.fechaDesde.substring(0, 10) : "2025-01-15";
      const contenido = [
        "codigo;fecha;cantidad;tipo",
        `340077;${fecha};1;S`,
        `N02BE01-02;${fecha};2;M`,
        `7087568;${fecha};3;I`,
      ].join("\n");
      const blob = new Blob([contenido], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = "plantilla_carga.csv"; a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.csv-panel  { background: #f8f9fa; }
.panel-body { padding: 16px; }

/* Drop zone */
.drop-zone {
  border: 2px dashed #adb5bd; border-radius: 8px;
  padding: 20px 16px; text-align: center;
  cursor: pointer; transition: all 0.2s; background: #fff;
}
.drop-zone:hover, .drop-zone.drag-over { border-color: #0d6efd; background: #f0f4ff; }
.drop-zone.has-file { border-color: #198754; background: #f0fff4; }
.drop-icon     { font-size: 36px; color: #adb5bd; display: block; margin-bottom: 6px; }
.drop-filename { font-weight: 600; font-size: 0.9rem; color: #198754; }
.drop-hint     { font-size: 0.85rem; color: #6c757d; }
.drop-link     { color: #0d6efd; text-decoration: underline; }
.drop-format   { font-size: 0.75rem; color: #adb5bd; margin-top: 6px; }
.drop-format code { background: #f0f0f0; padding: 1px 5px; border-radius: 3px; }

/* Mode bar */
.mode-bar { display: flex; gap: 4px; }
.mode-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 12px; border: 1px solid #dee2e6; border-radius: 6px;
  background: #fff; font-size: 0.8rem; color: #6c757d; cursor: pointer; transition: all 0.15s;
}
.mode-btn:hover  { border-color: #0d6efd; color: #0d6efd; }
.mode-btn.active { background: #e7f1ff; border-color: #0d6efd; color: #0d6efd; font-weight: 600; }

/* Tipo defecto */
.tipo-defecto { padding: 8px 10px; background: #fff; border-radius: 6px; border: 1px solid #dee2e6; }
.field-label  { font-weight: 600; font-size: 0.85rem; color: #495057; display: block; margin-bottom: 4px; }
.tipo-hint    { font-weight: 400; font-size: 0.72rem; color: #9ba3b2; margin-left: 4px; }
.tipo-btns    { display: flex; gap: 4px; flex-wrap: wrap; }
.tipo-btn {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; border: 1px solid #dee2e6; border-radius: 6px;
  background: #fff; font-size: 0.78rem; color: #6c757d; cursor: pointer; transition: all 0.15s;
}
.tipo-btn:hover  { border-color: #0d6efd; color: #0d6efd; }
.tipo-btn.active { background: #e7f1ff; border-color: #0d6efd; color: #0d6efd; font-weight: 700; }

/* Manual fecha */
.manual-fecha { padding: 8px 10px; background: #fff; border-radius: 6px; border: 1px solid #dee2e6; }

/* Preview table — 5 columnas */
.preview-table {
  border: 1px solid #dee2e6; border-radius: 6px;
  overflow: hidden; font-size: 0.8rem; max-height: 340px; overflow-y: auto;
}
.preview-header {
  display: grid; grid-template-columns: 90px 100px 56px 110px 1fr;
  background: #e9ecef; padding: 5px 8px; font-weight: 700; color: #495057; gap: 6px;
  position: sticky; top: 0; z-index: 1;
}
.preview-row {
  display: grid; grid-template-columns: 90px 100px 56px 110px 1fr;
  padding: 4px 8px; align-items: center; gap: 6px;
  border-top: 1px solid #f0f0f0; background: #fff; transition: background 0.1s;
}
.preview-row.row-alt   { background: #f8f9fa; }
.preview-row.row-error { background: #fff5f5; }
.preview-row.row-warn  { background: #fffbf0; }

.item-code   { font-weight: 700; color: #0d6efd; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.fecha-cell  { font-size: 0.78rem; color: #495057; }
.estado-cell { display: flex; align-items: center; }

.cantidad-input {
  width: 50px; padding: 2px 4px; border: 1px solid #ced4da;
  border-radius: 4px; font-size: 0.78rem; text-align: center;
}
.cantidad-input:focus    { outline: none; border-color: #0d6efd; }
.cantidad-input:disabled { background: #f8f9fa; color: #adb5bd; }

.tipo-select {
  width: 108px; padding: 2px 4px; border: 1px solid #ced4da;
  border-radius: 4px; font-size: 0.74rem; color: #343a40;
  background: #fff; cursor: pointer;
}
.tipo-select:focus    { outline: none; border-color: #0d6efd; }
.tipo-select:disabled { background: #f8f9fa; color: #adb5bd; }

/* Chips */
.chip {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 2px 7px; border-radius: 20px; font-size: 0.72rem; font-weight: 600;
  white-space: nowrap; max-width: 120px; overflow: hidden; text-overflow: ellipsis;
}
.chip-ok   { background: #e8f5e9; color: #198754; }
.chip-warn { background: #fff8e1; color: #f57c00; }
.chip-err  { background: #fdecea; color: #dc3545; }

/* Tabla vacía */
.tabla-vacia {
  display: flex; flex-direction: column; align-items: center;
  padding: 24px; color: #adb5bd; font-size: 0.85rem;
  border: 2px dashed #dee2e6; border-radius: 6px; background: #fff;
}

/* Resumen */
.resumen-box   { background: #fff; border: 1px solid #dee2e6; border-radius: 6px; padding: 10px 12px; font-size: 0.82rem; }
.resumen-title { font-weight: 700; font-size: 0.8rem; color: #6c757d; margin-bottom: 6px; }
.resumen-row   { display: flex; justify-content: space-between; padding: 2px 0; color: #495057; }

/* Progress */
.progreso-box   { background: #fff; border: 1px solid #dee2e6; border-radius: 6px; padding: 8px 12px; font-size: 0.8rem; }
.progreso-label { display: flex; align-items: center; gap: 6px; color: #495057; margin-bottom: 6px; }

/* Action buttons */
.btn-action {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 8px 14px; border: none; border-radius: 6px;
  font-size: 0.85rem; font-weight: 600; cursor: pointer; transition: all 0.15s; width: 100%;
}
.btn-action.success              { background: #198754; color: #fff; }
.btn-action.success:hover:not(:disabled) { background: #157347; }
.btn-action.info                 { background: #0dcaf0; color: #000; }
.btn-action.info:hover           { background: #0bb5d6; }
.btn-action.secondary            { background: #fff; color: #6c757d; border: 1px solid #dee2e6; }
.btn-action.secondary:hover:not(:disabled) { background: #f8f9fa; }
.btn-action:disabled             { opacity: 0.45; cursor: not-allowed; }

@keyframes spin { to { transform: rotate(360deg); } }
.spin { display: inline-block; animation: spin 1s linear infinite; }
</style>